import type { MobileStripBandSettings } from "../../mobileStripBandSettings"
import type { StripPlotArea } from "../stripLayout"
import type { RiskBandModel } from "./riskBandModel"

// SVG geometry for the SOLID band styles. The "dither" style renders per
// dot on a canvas instead (DitherFieldCanvas) and uses only the edge
// path from here; "solid" quantizes the boundary fade into
// stepped-opacity slices, and the legacy solid-core look renders a
// graduated fringe of fixed rows.

export type BandSvgLayer = { d: string; fillOpacity: number }
export type BandSvgArt = { layers: BandSvgLayer[]; bottomEdgePath: string }

type BandSvgInputs = {
  plot: StripPlotArea
  band: MobileStripBandSettings
  model: RiskBandModel
  yForValue: (value: number) => number
  timeForX: (x: number) => number
}

export const buildBandSvgArt = (inputs: BandSvgInputs): BandSvgArt => {
  const columns = sampleBandColumns(inputs)
  const bottomYs = columns.map(column => snapPinchedToTop(inputs, column.bottomY))
  return {
    layers: solidFillLayers(inputs, columns, bottomYs),
    bottomEdgePath: boundaryEdgePath(columns, bottomYs),
  }
}

type BandColumn = { x: number; t: number; bottomY: number }

// Lower-boundary samples every 8px, bleeding the full card width: the
// time axis extrapolates past the plot padding and the boundary series
// clamp flat at their ends, so the edges just continue the curve.
const sampleBandColumns = ({ plot, timeForX, model, yForValue }: BandSvgInputs): BandColumn[] => {
  const columns: BandColumn[] = []
  for (let x = 0; ; x += 8) {
    const clampedX = Math.min(x, plot.width)
    const t = timeForX(clampedX)
    columns.push({ x: clampedX, t, bottomY: yForValue(model.minValueAt(t)) })
    if (clampedX >= plot.width) break
  }
  return columns
}

// Where the boundary has pinched out against the frame ceiling, snap it
// to the strip's top edge so fill-to-top layers collapse there — no
// orphaned red sliver riding above a pinched-out band.
const snapPinchedToTop = ({ band, plot }: BandSvgInputs, y: number): number =>
  band.bandReach === "top" && y <= plot.padding.top + 0.75 ? 0 : y

// Closed region between two per-column boundaries.
const regionPath = (columns: BandColumn[], bottomYs: number[], topYs: number[]): string => {
  let d = `M ${columns[0].x} ${bottomYs[0]}`
  for (let i = 1; i < columns.length; i++) d += ` L ${columns[i].x} ${bottomYs[i]}`
  for (let i = columns.length - 1; i >= 0; i--) d += ` L ${columns[i].x} ${topYs[i]}`
  return d + " Z"
}

// The lower boundary as an open stroke path (bandEdgeStrokeWidthPx).
const boundaryEdgePath = (columns: BandColumn[], bottomYs: number[]): string => {
  let d = `M ${columns[0].x} ${bottomYs[0]}`
  for (let i = 1; i < columns.length; i++) d += ` L ${columns[i].x} ${bottomYs[i]}`
  return d
}

const solidFillLayers = (
  inputs: BandSvgInputs,
  columns: BandColumn[],
  bottomYs: number[],
): BandSvgLayer[] => {
  const { band } = inputs
  if (band.fillStyle !== "solid") return [] // canvas-rendered dither
  if (band.boundaryFade) return boundaryFadeLayers(inputs, columns, bottomYs)
  if (band.bandReach === "band") {
    const topYs = columns.map(column => inputs.yForValue(inputs.model.topValueAt(column.t)))
    return [{ d: regionPath(columns, bottomYs, topYs), fillOpacity: 1 }]
  }
  if (!band.bandSolidCore) {
    return [{ d: regionPath(columns, bottomYs, columns.map(() => 0)), fillOpacity: 1 }]
  }
  return fringeAndCoreLayers(inputs, columns, bottomYs)
}

// The boundary density fade as stepped-opacity slices from the boundary
// up to the fill top. "band" reach stops the fill at the block top —
// partway up the ramp — so density never completes there, like the
// desktop's authored pre-choice band.
const boundaryFadeLayers = (
  inputs: BandSvgInputs,
  columns: BandColumn[],
  bottomYs: number[],
): BandSvgLayer[] => {
  const { band } = inputs
  const rampTopYs = columns.map(column =>
    snapPinchedToTop(inputs, inputs.yForValue(inputs.model.maxValueAt(column.t))))
  const fillTopYs = band.bandReach === "band"
    ? columns.map(column => inputs.yForValue(inputs.model.topValueAt(column.t)))
    : rampTopYs
  const rampCoverage = band.bandReach === "band" ? band.bandThicknessFraction : 1
  const layers: BandSvgLayer[] = []
  for (let step = 0; step < band.boundaryFadeSteps; step++) {
    layers.push(fadeSlice(band, columns, bottomYs, fillTopYs, rampCoverage, step))
  }
  if (band.bandReach === "top") {
    // Full opacity from the ramp top to the strip's top edge.
    layers.push({
      d: regionPath(columns, rampTopYs, columns.map(() => 0)),
      fillOpacity: 1,
    })
  }
  return layers
}

const fadeSlice = (
  band: MobileStripBandSettings,
  columns: BandColumn[],
  bottomYs: number[],
  fillTopYs: number[],
  rampCoverage: number,
  step: number,
): BandSvgLayer => {
  const steps = band.boundaryFadeSteps
  const sliceBottomYs = bottomYs.map((y, i) => y + (fillTopYs[i] - y) * (step / steps))
  const sliceTopYs = bottomYs.map((y, i) => y + (fillTopYs[i] - y) * ((step + 1) / steps))
  // Density at the slice's midpoint of the min→max ramp.
  const density = band.boundaryFadeMinDensity
    + (1 - band.boundaryFadeMinDensity) * ((step + 0.5) / steps) * rampCoverage
  return { d: regionPath(columns, sliceBottomYs, sliceTopYs), fillOpacity: density }
}

// Graduated fringe: row 0 touches the boundary (sparsest), opacity ramps
// up row by row, then the full-opacity core takes over to the top.
const fringeAndCoreLayers = (
  { band }: BandSvgInputs,
  columns: BandColumn[],
  bottomYs: number[],
): BandSvgLayer[] => {
  const layers: BandSvgLayer[] = []
  for (let row = 0; row < band.fringeRows; row++) {
    const rowBottomYs = bottomYs.map(y => Math.max(y - row * band.fringeRowHeightPx, 0))
    const rowTopYs = bottomYs.map(y => Math.max(y - (row + 1) * band.fringeRowHeightPx, 0))
    layers.push({
      d: regionPath(columns, rowBottomYs, rowTopYs),
      fillOpacity: (row + 1) / (band.fringeRows + 1),
    })
  }
  const coreBottomYs = bottomYs.map(y =>
    Math.max(y - band.fringeRows * band.fringeRowHeightPx, 0))
  layers.push({
    d: regionPath(columns, coreBottomYs, columns.map(() => 0)),
    fillOpacity: 1,
  })
  return layers
}
